/**
 * What a memory knows, as people read it (packages/shared/src/schemas/memoryKnowledge.ts): decisions with their why,
 * conventions, architecture, how-tos (answered questions among them), glossary terms and summaries of each repository
 * and code area. Shown before the index (the extracted graph, which stays a part of its own): one section at a time,
 * newest first, an item opened in place with where it came from, what it rests on and what it replaced.
 */

import { KNOWLEDGE_SECTIONS, type KnowledgeSection, type KnowledgeView, type Sensitivity } from '@aioe/shared';
import { BookOpen } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Markdown } from './Markdown.tsx';
import { Button, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

const SENSITIVITY_TEXT: Record<Sensitivity, string> = { public: 'Public', internal: 'Internal', confidential: 'Confidential', restricted: 'Restricted' };
const SHOWN = 200;

const EMPTY: Record<KnowledgeSection, { title: string; body: string }> = {
  decisions: { title: 'No decisions yet', body: 'Decisions and their reasons arrive from the decision register and from what members’ workbenches distil from each repository.' },
  conventions: { title: 'No conventions yet', body: 'The standards a repository keeps — naming, layout, how tests are written — and the lessons agents learnt there.' },
  architecture: { title: 'No architecture yet', body: 'The parts of the system, where their boundaries are and how data moves between them, distilled by members’ workbenches.' },
  howtos: { title: 'No how-tos yet', body: 'Steps for common jobs, and questions agents have answered while working, kept once even when asked many times.' },
  glossary: { title: 'No terms yet', body: 'What the words this project uses mean, with the other names people use for them.' },
  summaries: { title: 'No summaries yet', body: 'A summary of each repository and each area of its code, made from what the memory already holds. Tidy now makes them.' },
};

const producerWord = (k: KnowledgeView) =>
  k.from === 'lesson' ? 'Lesson' : k.from === 'decision' ? 'Recorded decision' : k.producer === 'aioe' ? 'Made here from the memory' : k.producer === 'workbench' ? 'Distilled by a workbench' : 'Written by a person';

/**
 * The knowledge of a project's memory, or of the organisation's own when `projectId` is left out. `canWrite`: contributors
 * may run the tidy now; `onOpenIndexItem`: open an index item a piece of knowledge rests on.
 */
export function KnowledgePanel({ projectId, canWrite, onOpenIndexItem }: { projectId?: string; canWrite: boolean; onOpenIndexItem?: (itemId: string) => void }) {
  const tone = projectId ? VIEW_TONES.projects! : VIEW_TONES.memory!;
  const [section, setSection] = useState<KnowledgeSection>('decisions');
  const [past, setPast] = useState(false);
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<string>();
  const [tidy, setTidy] = useState<{ busy: boolean; said?: string; error?: string }>({ busy: false });
  const types = KNOWLEDGE_SECTIONS.find((s) => s.id === section)!.types;
  const list = useLoad(() => routes.memoryKnowledge(projectId, { type: types.join(','), superseded: past }), [projectId, section, past]);
  const items = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (list.data?.items ?? []).filter((k) => !f || k.title.toLowerCase().includes(f) || k.text.toLowerCase().includes(f) || k.rootId.toLowerCase().includes(f) || (k.terms ?? []).some((t) => t.toLowerCase().includes(f)));
  }, [list.data, filter]);
  const counts = list.data?.counts ?? {};
  const countOf = (id: KnowledgeSection) => KNOWLEDGE_SECTIONS.find((s) => s.id === id)!.types.reduce((n, t) => n + (counts[t] ?? 0), 0);
  const runTidy = async () => {
    setTidy({ busy: true });
    try {
      const out = await routes.tidyMemoryKnowledge(projectId);
      setTidy({ busy: false, said: `${out.answers} answered ${out.answers === 1 ? 'question' : 'questions'} and ${out.summaries} ${out.summaries === 1 ? 'summary' : 'summaries'} up to date` });
      list.reload();
    } catch (e) {
      setTidy({ busy: false, error: problemText(e) });
    }
  };
  return (
    <ListGroup
      title="Knowledge"
      count={list.data ? items.length : undefined}
      description="What the memory knows, in words: kept once, dated, with where it came from. Agents are given it before anything else."
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onPress={() => (setPast(!past), setOpen(undefined))}>
            {past ? 'Hide replaced' : 'Show replaced'}
          </Button>
          {canWrite ? (
            <Button size="sm" variant="secondary" isPending={tidy.busy} onPress={() => void runTidy()}>
              Tidy now
            </Button>
          ) : null}
        </div>
      }
    >
      <ListBlock className="grid gap-3">
        <div className="max-w-full overflow-x-auto">
          <Segment aria-label="Which knowledge" size="sm" selectedKey={section} onSelectionChange={(k) => (setSection(String(k) as KnowledgeSection), setOpen(undefined))} className="w-fit">
            {KNOWLEDGE_SECTIONS.map((s) => (
              <Segment.Item key={s.id} id={s.id}>
                {s.label}
                {list.data && section !== s.id && countOf(s.id) > 0 ? <span className="ml-1 tabular-nums text-muted">{countOf(s.id)}</span> : null}
              </Segment.Item>
            ))}
          </Segment>
        </div>
        <input className={inputClass} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by words, term or repository" aria-label="Filter" />
        {tidy.said ? <StatusMark state="connected" label={tidy.said} wrap /> : null}
        <ErrorNote error={tidy.error ?? list.error} />
      </ListBlock>
      {!list.data && !list.error ? <ListBlock className="text-sm text-muted">Reading what the memory knows…</ListBlock> : null}
      {list.data && items.length === 0 ? (
        <ListBlock>
          {!filter && list.data.itemsFiltered > 0 && countOf(section) === 0 ? (
            <Empty tone={tone} icon={<BookOpen />} compact title="Nothing here you can read" body={`${list.data.itemsFiltered} ${list.data.itemsFiltered === 1 ? 'item is' : 'items are'} hidden from you by their labels.`} />
          ) : (
            <Empty tone={tone} icon={<BookOpen />} compact title={filter ? 'Nothing matches' : EMPTY[section].title} body={filter ? 'Try other words, or another section.' : EMPTY[section].body} />
          )}
        </ListBlock>
      ) : null}
      {items.slice(0, SHOWN).flatMap((k) => [
        <ListRow
          key={k.id}
          title={k.title}
          description={`${producerWord(k)}${k.rootId ? ` · ${k.rootId}` : ''} · ${SENSITIVITY_TEXT[k.label.sensitivity]}${k.label.compartments.length ? `, ${k.label.compartments.join(', ')}` : ''} · ${timeAgo(k.updatedAt)}`}
          status={k.status !== 'current' ? <StatusMark state="disabled" label={k.status === 'superseded' ? 'Replaced' : 'Retired'} /> : k.outcome === 'dead_end' ? <StatusMark state="medium" label="Dead end" /> : undefined}
          onPress={() => setOpen(open === k.id ? undefined : k.id)}
        />,
        open === k.id ? <KnowledgeDetail key={`${k.id}/detail`} projectId={projectId} item={k} onOpen={(id) => setOpen(id)} {...(onOpenIndexItem ? { onOpenIndexItem } : {})} /> : null,
      ])}
      {items.length > SHOWN ? <ListBlock className="text-xs text-muted">The newest {SHOWN} of {items.length}. Filter to narrow it.</ListBlock> : null}
    </ListGroup>
  );
}

function KnowledgeDetail({ projectId, item, onOpen, onOpenIndexItem }: { projectId?: string; item: KnowledgeView; onOpen: (id: string) => void; onOpenIndexItem?: (itemId: string) => void }) {
  const detail = useLoad(() => (item.from ? Promise.resolve(undefined) : routes.memoryKnowledgeItem(projectId, item.id)), [projectId, item.id]);
  const d = detail.data;
  const sources = item.sources
    .map((s) => [s.ref, s.rootId, s.files?.slice(0, 3).join(', '), s.commit ? `@ ${s.commit.slice(0, 8)}` : undefined].filter(Boolean).join(' '))
    .filter(Boolean);
  return (
    <ListBlock className="grid gap-3 text-sm">
      <Markdown text={item.text} fallback={<p className="m-0 text-muted">It says nothing more than its title.</p>} />
      {item.why ? (
        <div>
          <div className="text-xs text-muted">Why</div>
          <Markdown text={item.why} compact />
        </div>
      ) : null}
      {item.terms?.length ? <div className="text-xs text-muted">Also called or about: {item.terms.join(', ')}</div> : null}
      <div className="text-xs text-muted">
        {producerWord(item)}
        {item.model ? ` with ${item.model}` : ''}
        {item.since ? ` · true since ${new Date(item.since).toLocaleDateString()}` : ''}
        {sources.length ? ` · from ${sources.slice(0, 4).join('; ')}` : ''}
        {` · version ${item.version}`}
      </div>
      <ErrorNote error={detail.error} />
      {d && d.linked.length > 0 ? (
        <div>
          <div className="text-xs text-muted">Rests on</div>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {d.linked.slice(0, 16).map((l) => (
              <li key={l.id}>
                {onOpenIndexItem ? (
                  <button type="button" className="cursor-[var(--cursor-interactive)] text-accent" onClick={() => onOpenIndexItem(l.id)}>
                    {l.name}
                  </button>
                ) : (
                  l.name
                )}
                {l.sourceFile ? <span className="mono text-xs text-muted"> {l.sourceFile}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {d?.replacedBy ? (
        <div className="text-xs text-muted">
          Replaced by{' '}
          <button type="button" className="cursor-[var(--cursor-interactive)] text-accent" onClick={() => onOpen(d.replacedBy!.id)}>
            {d.replacedBy.title}
          </button>
        </div>
      ) : null}
      {d && d.replaced.length > 0 ? <div className="text-xs text-muted">Replaces {d.replaced.map((r) => r.title).join('; ')}</div> : null}
    </ListBlock>
  );
}
